import { useMemo, useState } from 'react';

import { useDeletePeerImage } from '@givebacks/api/store';
import { AxiosError, Constants, isAxiosError } from 'core';
import { useFormListValidation } from 'core/Hooks';
import { getErrorMessage_v2 } from 'core/Utils';
import { ButtonMantine, Group, Modal, Stack, notifications } from 'ui';
import { UseFormReturnType } from 'ui/Form';

import { useCrowdfundingProvider, useManagementTranslations } from '../../../../../../Context';
import { PeerTypes, useShowCampaignSettings, useShowCurrentCampaign } from '../../../../../../api';
import { useCreateCampaignPeer } from '../../../../../../api/new/campaigns/peers/create';
import { useUpdateCampaignPeer } from '../../../../../../api/new/campaigns/peers/update';
import { usePeerName } from '../../../hooks/usePeerName';
import { AddEditPeerContent, ParticipantFormType, TeamFormType } from './AddEditPeerForms';

type PeerModalProps = {
  uuid?: string;
  opened: boolean;
  onClose: () => void;
};

type PeerForms = {
  team: UseFormReturnType<TeamFormType>;
  participant: UseFormReturnType<ParticipantFormType>;
};

const PeerModalFooter = ({
  uuid,
  onClose,
  peerType,
}: {
  peerType: PeerTypes;
  uuid?: string;
  onClose: () => void;
}) => {
  const { peerName, peer_type } = usePeerName();
  const { campaignId, causeId } = useCrowdfundingProvider();
  const { t } = useManagementTranslations();

  const { mutate: createPeer, isPending: isCreatePeerLoading } = useCreateCampaignPeer({
    peerType,
  });

  const { mutate: updateCampaignPeer, isPending: isUpdateCampaignPeerLoading } =
    useUpdateCampaignPeer({ peerType });

  const { mutateAsync: deletePeerImage } = useDeletePeerImage();

  const { validateForms, useGetForms } = useFormListValidation<PeerForms>();

  const forms = useGetForms();
  const team = forms.team?.values;
  const participant = forms.participant?.values;

  const teamRequest = useMemo(() => {
    const request = {
      name: team?.name,
      email: team?.email,
      goal: team?.goal,
    };

    if (team?.file) {
      Object.assign(request, { image: team?.file });
    }

    return request;
  }, [team?.email, team?.file, team?.goal, team?.name]);

  const participantRequest = useMemo(() => {
    const request = {
      first_name: participant?.first_name,
      last_name: participant?.last_name,
      email: participant?.email,
      team_id: participant?.team_id,
      goal: participant?.goal !== '' ? participant?.goal : '0',
      options: participant?.options,
    };

    if (participant?.file) {
      Object.assign(request, { image: participant?.file });
    }

    return request;
  }, [participant]);

  const request = peerType === 'teams' ? teamRequest : participantRequest;

  const handleCreatePeer = () => {
    if (!campaignId || !causeId) return;
    createPeer(
      { ...request, campaignId, causeId },
      {
        onSuccess() {
          notifications.show({ message: `New ${peerName.singular} created!`, variant: 'success' });
          onClose();
        },
        onError(e) {
          notifications.show({
            message: getErrorMessage_v2(e) ?? Constants.defaultErrorMessage,
            variant: 'error',
          });
        },
      }
    );
  };

  const handleUpdatePeer = () => {
    if (!uuid || !campaignId || !causeId) return;
    const updateTeamRequest = Object.assign(teamRequest, { uuid: uuid });
    const updateParticipantRequest = Object.assign(participantRequest, { uuid: uuid });

    updateCampaignPeer(
      {
        campaignId,
        causeId,
        ...(peerType === 'teams' ? updateTeamRequest : updateParticipantRequest),
      },
      {
        onSuccess() {
          if (peer_type === 'teams') {
            notifications.show({ message: `${t('team')} info updated!`, variant: 'success' });
          } else {
            notifications.show({
              message: `${t('participant')} info updated!`,
              variant: 'success',
            });
          }
          onClose();
        },
        onError(e) {
          const error = e as Error | AxiosError;
          if (peer_type === 'teams') {
            if (isAxiosError(error) && error?.response?.status === 422) {
              notifications.show({
                message: `Error: ${t('team')} name must be unique. Please enter another team name.`,
                variant: 'error',
              });
              forms.team.setFieldError('name', `${t('team')} name is already taken`);
            } else {
              notifications.show({
                message: `Error: Could not update ${t('team').toLowerCase()} information.`,
                variant: 'error',
              });
            }
          } else {
            notifications.show({
              message: getErrorMessage_v2(e) ?? Constants.defaultErrorMessage,
              variant: 'error',
            });
            // notifications.show({
            //   message: isAxiosError(error)
            //     ? error?.response?.data.error
            //     : `Error: Could not update ${t('participant').toLowerCase()} information.`,
            //   variant: 'error',
            // });
          }
        },
      }
    );
  };

  const handleDeleteTeamImage = async () => {
    if (forms.team?.isDirty('image') && team.image === undefined) {
      if (team.file) return true; // If swapping the image, don't delete the old one, we'll just run the update.
      if (!uuid || !peer_type || !causeId || !campaignId) return;
      try {
        await deletePeerImage({ peerId: uuid, campaignId, causeId, peerType: peer_type });
        notifications.show({ message: `${t('team')} image deleted!`, variant: 'success' });
        return true;
      } catch {
        notifications.show({
          message: `Error: Failed to delete ${t('team').toLowerCase()} image.`,
          variant: 'error',
        });
        return false;
      }
    } else {
      return true;
    }
  };
  const handleDeleteParticipantImage = async () => {
    if (forms.participant?.isDirty('image') && participant.image === undefined) {
      if (participant.file) return true; // If swapping the image, don't delete the old one, we'll just run the update.
      if (!uuid || !peer_type || !causeId || !campaignId) return;
      try {
        await deletePeerImage({ peerId: uuid, campaignId, causeId, peerType: peer_type });
        notifications.show({ message: `${t('participant')} image deleted!`, variant: 'success' });
        return true;
      } catch {
        notifications.show({
          message: `Error: Failed to delete ${t('participant').toLowerCase()} image.`,
          variant: 'error',
        });
        return false;
      }
    } else {
      return true;
    }
  };

  const handleSave = async () => {
    const formIsDirty = peer_type === 'teams' ? forms.team.isDirty() : forms.participant.isDirty();
    if (validateForms().isValid) {
      if (uuid) {
        if (formIsDirty) {
          await (peer_type === 'teams' ? handleDeleteTeamImage() : handleDeleteParticipantImage());
          handleUpdatePeer();
        } else {
          onClose();
        }
      } else {
        handleCreatePeer();
      }
    }
  };

  return (
    <Group justify="flex-end">
      <ButtonMantine size="md" variant="subtle" onClick={onClose}>
        Cancel
      </ButtonMantine>
      <ButtonMantine
        size="md"
        w={160}
        onClick={handleSave}
        loading={isCreatePeerLoading || isUpdateCampaignPeerLoading}
      >
        {uuid ? 'Save Changes' : 'Save'}
      </ButtonMantine>
    </Group>
  );
};

export const AddEditPeerModal = ({ uuid, opened, onClose }: PeerModalProps) => {
  const { peerName, peer_type } = usePeerName();

  const modalTitle = useMemo(() => {
    if (uuid) {
      return `Edit ${peerName.singular} Info`;
    } else {
      return `Add New ${peerName.singular}`;
    }
  }, [peerName, uuid]);
  return (
    <Modal opened={opened} onClose={onClose} title={modalTitle}>
      <Stack
        gap="lg"
        pt="lg"
        style={(theme) => ({ borderTop: `1px solid ${theme?.colors.gray[1]}` })}
      >
        <AddEditPeerContent peer_uuid={uuid} peer_type={peer_type as PeerTypes} />
        {peer_type && <PeerModalFooter uuid={uuid} onClose={onClose} peerType={peer_type} />}
      </Stack>
    </Modal>
  );
};

export const AddEditPeerModalButton = ({ label }: { label: string }) => {
  const { data: campaign } = useShowCurrentCampaign();
  const { data: campaignSettings } = useShowCampaignSettings({
    campaignId: campaign?.uuid,
    causeId: campaign?.cause_id,
  });
  const { isCampaignReadOnly } = campaignSettings;
  const [isAddTeamOpened, setIsAddTeamOpened] = useState(false);

  return (
    <>
      {!isCampaignReadOnly && (
        <ButtonMantine size="sm" variant="subtle" onClick={() => setIsAddTeamOpened(true)}>
          {label}
        </ButtonMantine>
      )}
      <AddEditPeerModal opened={isAddTeamOpened} onClose={() => setIsAddTeamOpened(false)} />
    </>
  );
};
